import React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { Translate } from 'react-localize-redux';
import LoadingState from './common/LoadingState';
import ErrorStateView from './common/ErrorState';

export interface CohortSummaryData {
    patients: number;
    closedVisits: number;
    lesions: number;
    images: number;
}

export interface CohortSummaryCardPureProps {
    loading: boolean;
    error?: Error | null;
    data: CohortSummaryData | null;
    onRetry?: () => void;
}

const EMPTY_COHORT: CohortSummaryData = {
    patients: 0,
    closedVisits: 0,
    lesions: 0,
    images: 0,
};

const Counter: React.FC<{ labelId: string; value: number }> = ({ labelId, value }) => (
    <Box sx={{ textAlign: 'center', p: 2 }}>
        <Typography variant="h4" component="p">{value.toLocaleString()}</Typography>
        <Typography variant="body2" color="textSecondary">
            <Translate id={labelId} />
        </Typography>
    </Box>
);

export const CohortSummaryCardPure: React.FC<CohortSummaryCardPureProps> = ({
    loading,
    error,
    data,
    onRetry,
}) => {
    if (loading) return <LoadingState />;
    if (error) return <ErrorStateView error={error} onRetry={onRetry} />;

    const cohort = data ?? EMPTY_COHORT;
    const isEmpty = Object.values(cohort).every(v => v === 0);

    if (isEmpty) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="textSecondary">
                    <Translate id="hospital.analytics.clinical.cohort-summary.empty" />
                </Typography>
            </Box>
        );
    }

    return (
        <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
                <Counter labelId="hospital.analytics.clinical.cohort-summary.patients" value={cohort.patients} />
            </Grid>
            <Grid item xs={6} md={3}>
                <Counter labelId="hospital.analytics.clinical.cohort-summary.closed-visits" value={cohort.closedVisits} />
            </Grid>
            <Grid item xs={6} md={3}>
                <Counter labelId="hospital.analytics.clinical.cohort-summary.lesions" value={cohort.lesions} />
            </Grid>
            <Grid item xs={6} md={3}>
                <Counter labelId="hospital.analytics.clinical.cohort-summary.images" value={cohort.images} />
            </Grid>
        </Grid>
    );
};

const CohortSummaryCard = CohortSummaryCardPure;
export default CohortSummaryCard;
